import React, { useState, useEffect, useCallback } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { USERS } from "../../Pages/Home";
import { buildApiUrl } from "../../config/api";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  width: "calc(100% - 32px)",
  maxWidth: 480,
  maxHeight: "80vh",
  overflowY: "auto",
};

function readLocalBookings() {
  return JSON.parse(localStorage.getItem("kokorin_bookings") || "[]");
}

export default function ActiveBookings({ refreshSignal }) {
  const [bookings, setBookings] = useState(readLocalBookings);
  const [open, setOpen] = useState(false);
  const [serverStatuses, setServerStatuses] = useState({});
  const [confirmToken, setConfirmToken] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    setBookings(readLocalBookings());
  }, [refreshSignal]);

  const fetchStatuses = useCallback(async (list) => {
    const results = {};
    await Promise.allSettled(
      list.map(async (b) => {
        try {
          const res = await fetch(buildApiUrl(`/api/requests/by-token/${b.cancelToken}`));
          if (res.ok) {
            results[b.cancelToken] = await res.json();
          }
        } catch {}
      })
    );
    setServerStatuses(results);
  }, []);

  useEffect(() => {
    if (open && bookings.length) {
      fetchStatuses(bookings);
    }
  }, [open, bookings, fetchStatuses]);

  const handleCancel = async (token) => {
    setCancelling(true);
    try {
      const res = await fetch(buildApiUrl(`/api/requests/cancel/${token}`), {
        method: "POST",
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Помилка");
      }
      const updated = readLocalBookings().filter((b) => b.cancelToken !== token);
      localStorage.setItem("kokorin_bookings", JSON.stringify(updated));
      setBookings(updated);
      setConfirmToken(null);
      setServerStatuses((prev) => {
        const next = { ...prev };
        delete next[token];
        return next;
      });
    } catch (err) {
      alert(err.message);
    } finally {
      setCancelling(false);
    }
  };

  const trainerLabel = (id) =>
    USERS.find((u) => u.id === id)?.label || id;

  if (!bookings.length) return null;

  const confirmBooking = bookings.find((b) => b.cancelToken === confirmToken);

  return (
    <>
      <button
        className="active-bookings-card"
        onClick={() => setOpen(true)}
      >
        Мої записи · {bookings.length}
      </button>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setConfirmToken(null);
        }}
      >
        <div className="modal_container">
          <Box sx={modalStyle}>
            {confirmToken ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Підтвердження скасування
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Скасувати запис?
                  <br />
                  <strong>
                    {trainerLabel(confirmBooking?.trainer)} •{" "}
                    {confirmBooking?.date} • {confirmBooking?.time}
                  </strong>
                </Typography>
                <div style={{ display: "flex", gap: 12 }}>
                  <button
                    onClick={() => handleCancel(confirmToken)}
                    disabled={cancelling}
                    className="button cancel-btn"
                    style={{ flex: 1 }}
                  >
                    {cancelling ? "..." : "Скасувати"}
                  </button>
                  <button
                    onClick={() => setConfirmToken(null)}
                    className="button reserve"
                    style={{ flex: 1 }}
                  >
                    Назад
                  </button>
                </div>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Мої активні записи
                </Typography>
                <div className="active-bookings-list">
                  {bookings.map((b) => {
                    const srv = serverStatuses[b.cancelToken];
                    const isCancelled = srv?.status === "failed";
                    return (
                      <div
                        key={b.cancelToken}
                        className={`active-booking-item${isCancelled ? " cancelled" : ""}`}
                      >
                        <div className="active-booking-info">
                          <strong>{trainerLabel(b.trainer)}</strong>
                          <span>
                            {b.date} · {b.time}
                          </span>
                          <span>
                            {b.name} {b.surname}
                          </span>
                          {isCancelled && (
                            <span className="cancelled-badge">Скасовано</span>
                          )}
                        </div>
                        {!isCancelled && (
                          <button
                            onClick={() => setConfirmToken(b.cancelToken)}
                            className="cancel-btn-small"
                          >
                            Скасувати
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="button reserve"
                  style={{ marginTop: 4 }}
                >
                  Закрити
                </button>
              </>
            )}
          </Box>
        </div>
      </Modal>
    </>
  );
}
